import moment from 'moment';

/** `EDIT_SOMETHING` → "Edit something": the text of an action with no label yet. */
const humanizeAction = (action) => {
  const text = String(action || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};

/**
 * What the user did, as a sentence fragment ("Avasi projektin"): the
 * translation of `activity_log.action.<ACTION>`, or the action code made
 * readable for an action that has none yet.
 * @param {Function} t i18next t
 * @param {string} action e.g. 'VIEW_PROJECT'
 * @returns {string}
 */
export const getActivityActionLabel = (t, action) =>
  t(`activity_log.action.${action}`, humanizeAction(action));

// Targets whose name adds to the sentence (an image's file name, a record's
// day); a project target's name is the project the feed belongs to.
const NAMED_TARGET_TYPES = ['FILEPERMISSION', 'IMAGE', 'RECORD'];

// The plural (count ≥ 2) wording of a merged row over several targets, by
// action: `activity_log.action_count.<ACTION>` (`_plural` for the plural).
// An action missing here is counted in times ("… 3 kertaa") instead.
const ACTION_COUNT_TEXTS = {
  EDIT_PROJECT: 'Muokkasi {{count}} projektia',
  EDIT_RECORD: 'Muokkasi {{count}} kirjausta',
  VIEW_IMAGE: 'Katsoi {{count}} kuvaa',
  VIEW_PROJECT: 'Avasi {{count}} projektia',
  VIEW_RECORD: 'Avasi {{count}} kirjausta',
  VIEW_TIMESTAMPS: 'Avasi {{count}} projektin aikaleimat',
};

/**
 * What a row merged from several events did (see mergeRepeatedActivity):
 * over several targets their count ("Katsoi 12 kuvaa"), over one (or none,
 * as the timestamps) the times ("Avasi aikaleimat 4 kertaa").
 */
const getMergedActionText = (t, item) => {
  if (item.mergedTargetCount > 1 && ACTION_COUNT_TEXTS[item.action]) {
    return t(
      `activity_log.action_count.${item.action}`,
      ACTION_COUNT_TEXTS[item.action],
      { count: item.mergedTargetCount }
    );
  }
  return t('activity_log.action_repeated', '{{action}} {{count}} kertaa', {
    action: getActivityActionLabel(t, item.action),
    count: item.mergedCount,
  });
};

/**
 * The row's action with the target's name when it tells something:
 * "Katsoi kuvaa · IMG_0042.jpg". A row merged from several events
 * (mergeRepeatedActivity) counts them: "Katsoi 12 kuvaa", or, the same
 * target again, "Katsoi kuvaa 2 kertaa · IMG_0042.jpg".
 */
export const getActivityDescription = (t, item) => {
  const merged = item.mergedCount > 1;
  const label = merged
    ? getMergedActionText(t, item)
    : getActivityActionLabel(t, item.action);
  if (
    !item.targetName ||
    !NAMED_TARGET_TYPES.includes(item.targetType) ||
    (merged && item.mergedTargetCount > 1)
  ) {
    return label;
  }
  // A record is named by its day (YYYY-MM-DD).
  const name = /^\d{4}-\d{2}-\d{2}$/.test(item.targetName)
    ? moment(item.targetName).format('D.M.YYYY')
    : item.targetName;
  return `${label} · ${name}`;
};

/**
 * When it happened, as short as is unambiguous: the time today, the day and
 * the time this year, the date before that.
 * @param {string} createdAt ISO timestamp
 * @param {moment.Moment} [now]
 */
export const formatActivityTime = (createdAt, now = moment()) => {
  const at = moment(createdAt);
  if (at.isSame(now, 'day')) return at.format('HH:mm');
  if (at.isSame(now, 'year')) return at.format('D.M. HH:mm');
  return at.format('D.M.YYYY');
};

/**
 * The second row of an activity dialog's header, the same in the project's
 * day dialog and a user's: the weekday and the date ("Torstai 16.10.2026")
 * and, given a `count`, the events counted ("· Tapahtumia: 8").
 * @param {Function} t i18next t
 * @param {moment.Moment|string} day the day (a moment, or YYYY-MM-DD)
 * @param {?number} [count] the events shown; null or undefined for none
 * @returns {string}
 */
export const formatActivityDaySubtitle = (t, day, count) => {
  const date = moment(
    day,
    moment.isMoment(day) ? undefined : 'YYYY-MM-DD'
  ).format('dddd L');
  const label = date.charAt(0).toUpperCase() + date.slice(1);
  if (count === null || count === undefined) return label;
  return `${label} · ${t(
    'activity_log.activity_count',
    'Tapahtumia: {{count}}',
    {
      count,
    }
  )}`;
};

/** Two letters for an avatar without a picture: "Tatu Aalto" → "TA". */
export const getInitials = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

/**
 * The feed's rows grouped by the local day they happened on, newest first.
 * @param {Array<Object>} items
 * @returns {Array<{day: string, items: Array<Object>}>} day as YYYY-MM-DD
 */
export const groupActivityByDay = (items) => {
  const groups = [];
  (items || []).forEach((item) => {
    const day = moment(item.createdAt).format('YYYY-MM-DD');
    const last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.items.push(item);
    } else {
      groups.push({ day, items: [item] });
    }
  });
  return groups;
};

/**
 * The feed's rows from `since` on (a moment, e.g. the local midnight).
 * @param {Array<Object>} items
 * @param {moment.Moment} since
 */
export const activitySince = (items, since) => {
  const from = since.valueOf();
  return (items || []).filter((item) => {
    const at = Date.parse(item.createdAt);
    return Number.isFinite(at) && at >= from;
  });
};

// Who did it: the user's id, or, for a row without one, their name.
const activityUserKey = (item) =>
  item.userId ? `id:${item.userId}` : `name:${item.userName || ''}`;

/**
 * The feed's rows grouped by who did them, the user with the newest row
 * first, each user's rows newest first. A row without a userId is grouped by
 * its userName.
 * @param {Array<Object>} items
 * @returns {Array<{key: string, userId: ?string, userName: ?string,
 *   items: Array<Object>}>}
 */
export const groupActivityByUser = (items) => {
  const newestFirst = [...(items || [])].sort((a, b) =>
    (b.createdAt || '').localeCompare(a.createdAt || '')
  );
  const groups = new Map();
  newestFirst.forEach((item) => {
    const key = activityUserKey(item);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        userId: item.userId || null,
        userName: item.userName || null,
        items: [],
      });
    }
    groups.get(key).items.push(item);
  });
  return [...groups.values()];
};

/**
 * The rows with each user's repeated events folded into one: the same user
 * (id, or name without one), action and target type. By default all of them
 * in `items` (the overview's summary of a user's day, where each merged row
 * stands at its newest event); `consecutive` merges only unbroken runs,
 * keeping a timeline's order truthful. A lone event is handed back as it is;
 * a merged row is its newest event with
 *   mergedCount        the events it stands for,
 *   mergedTargetCount  the different targets among them (by id, else name),
 *   mergedFirstAt      the oldest event's createdAt,
 *   mergedItems        the events, in the order given.
 * The order of `items` (newest first in the feed) is kept.
 * @param {Array<Object>} items
 * @param {{consecutive?: boolean}} [options]
 * @returns {Array<Object>}
 */
export const mergeRepeatedActivity = (items, { consecutive = false } = {}) => {
  const runs = [];
  const runByKey = new Map();
  (items || []).forEach((item) => {
    const key = [
      activityUserKey(item),
      item.action || '',
      item.targetType || '',
    ].join('|');
    const last = runs[runs.length - 1];
    const run = consecutive
      ? last && last.key === key
        ? last
        : null
      : runByKey.get(key);
    if (run) {
      run.items.push(item);
    } else {
      const created = { key, items: [item] };
      runs.push(created);
      runByKey.set(key, created);
    }
  });
  return runs.map(({ items: events }) => {
    if (events.length === 1) return events[0];
    const byTime = [...events].sort((a, b) =>
      (a.createdAt || '').localeCompare(b.createdAt || '')
    );
    const targets = new Set(
      events.map((event) => event.targetId || event.targetName || '')
    );
    return {
      ...byTime[byTime.length - 1],
      mergedCount: events.length,
      mergedTargetCount: targets.size,
      mergedFirstAt: byTime[0].createdAt,
      mergedItems: events,
    };
  });
};
